// @flow

import * as React from 'react';
import {useContext, useRef} from 'react';
import {contextStubFunction} from 'docs/util';

export type FileRefContextType = {|
  fileRef: React.ElementRef<any>,
  getScrollPosition: () => number,
  getHeight: () => number,
  setScrollPosition: (position: number, animate?: boolean) => void,
|};

const FileRefContext = React.createContext<FileRefContextType>({
  fileRef: null,
  getScrollPosition: contextStubFunction('FileRef'),
  getHeight: contextStubFunction('FileRef'),
  setScrollPosition: contextStubFunction('FileRef'),
});

type FileRefProviderProps = {|
  children: React.Node,
|};

export const FileRefProvider = ({children}: FileRefProviderProps) => {
  const fileRef = useRef();

  const getFileElement = (): ?HTMLElement => {
    if (!fileRef?.current) {
      return null;
    }
    return fileRef.current;
  };

  const getScrollPosition = () => {
    const contentEle = getFileElement();
    if (!contentEle) {
      return -1;
    }
    return contentEle.scrollTop;
  };

  const setScrollPosition = (position: number, animate?: boolean = false) => {
    const contentEle = getFileElement();

    if (!contentEle) {
      return;
    }
    contentEle.scrollTop = position;
  };

  const getHeight = () => {
    const contentEle = getFileElement();
    if (!contentEle) {
      return -1;
    }
    return contentEle.clientHeight;
  };

  const context = {
    fileRef,
    getScrollPosition,
    setScrollPosition,
    getHeight,
  };

  return (
    <FileRefContext.Provider value={context}>
      {children}
    </FileRefContext.Provider>
  );
};

export const useFileRef = () => {
  return useContext(FileRefContext);
};
